import { Component, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PERMISSION } from '@lib/core/enum';
import { QueryParams } from '@proget-shared';
import { DialogService } from '@proget-shared/dialog';
import { GridAction, GridActionPrompt, GridActionType, GridActionsModule, SelectionStrategy } from '@proget-shared/grid/grid-actions';
import { GridControl, GridControlService, PagingList } from '@proget-shared/grid/grid-control';
import { SelectionService } from '@proget-shared/grid/selection';
import { TableModule } from '@proget-shared/grid/table';
import { TableActionsModule } from '@proget-shared/grid/table-actions';
import { QueryParamsService } from '@proget-shared/query-params';
import { TranslateModule } from '@proget-shared/translate';
import { EllipsisModule } from '@proget-shared/ui/ellipsis';
import { LinkModule } from '@proget-shared/ui/link';
import { TableCellComponent } from '@proget-shared/ui/table-cell';
import { combineLatest, startWith, Subject, switchMap, take } from 'rxjs';

import { GenerateQrCodeComponent } from '../../../component/generate-qr-code/generate-qr-code.component';
import { DeviceBackup, User } from '../../../model';
import { UserCardService } from '../../../service/user-card.service';
import { UserCardRepository } from '../../../user-card.repository';

@Component({
  selector: 'lib-backups',
  templateUrl: './backups.component.html',
  styleUrl: './backups.component.scss',
  imports: [
    GridActionsModule,
    TableModule,
    TableActionsModule,
    TableCellComponent,
    EllipsisModule,
    LinkModule,
    TranslateModule,
  ],
  providers: [GridControlService, QueryParamsService, SelectionService],
})
export class BackupsComponent {
  protected readonly gridActions: GridAction<DeviceBackup>[] = [
    {
      label: 'device_card.tab.backup.action.delete',
      icon: 'icon-delete-icon',
      permission: PERMISSION.USER_CARD_ALLOW_MANAGE,
      enabled: (item: DeviceBackup): boolean => item.isSnapshot,
      prompt: {
        type: GridActionPrompt.CONFIRM,
        header: 'device_card.tab.backup.action.delete',
        message: 'confirm_action.message',
      },
      batchWorkConfiguration: {
        actionKey: 'device_card.tab.backup.action.delete',
      },
      type: GridActionType.BATCH_WORK,
      action: (itemId) => this.userCardRepository.deleteBackup(itemId),
      finalize: {
        selectionStrategy: SelectionStrategy.ALWAYS_DESELECT,
        callback: (): void => {
          this.reloadList();
        },
      },
    },
  ];
  protected readonly gridControl: GridControl;

  protected pagingList: PagingList<DeviceBackup> | undefined;
  protected requestPending = false;

  protected readonly userBackupSelection = inject(SelectionService);

  private readonly destroyRef = inject(DestroyRef);
  private readonly dialogService = inject(DialogService);
  private readonly userCardRepository = inject(UserCardRepository);
  private readonly userCardService = inject(UserCardService);
  private readonly reloadSubject = new Subject<void>();

  constructor() {
    const gridControlService = inject(GridControlService);

    this.gridControl = gridControlService.registerGridControl('user-card/backups');

    combineLatest([
      this.userCardService.user$,
      this.gridControl.requestParams$,
      this.reloadSubject.pipe(startWith(null)),
    ])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ([user, params]) => {
          this.loadList(user, params);
        },
      });

    this.destroyRef.onDestroy(() => {
      this.reloadSubject.complete();
    });
  }

  protected generateQRCode(): void {
    if (this.requestPending) {
      return;
    }

    this.requestPending = true;
    this.userCardService.user$
      .pipe(
        take(1),
        switchMap(({ uuid }) => this.userCardRepository.generateBackupQrCode(uuid)),
        switchMap((qrCode) => this.dialogService.custom(GenerateQrCodeComponent, { configuration: { qrCode } }))
      )
      .subscribe({
        complete: () => {
          this.requestPending = false;
        },
      });
  }

  private loadList(user: User, params: QueryParams): void {
    this.userCardRepository
      .getAllBackups(user.uuid, params)
      .subscribe({
        next: (items) => {
          this.pagingList = items;
          this.gridControl.setItemsCount(this.pagingList.total);
          this.userBackupSelection.setVisibleItems(this.pagingList.items);
        },
        error: () => {
          this.pagingList = new PagingList();
        },
      });
  }

  private reloadList(): void {
    this.reloadSubject.next();
  }
}
